// src/modules/gyms/hooks/use-branch-actions.ts

import { useCallback } from "react";
import { gymPartnerService } from "../services/gym.service";
import { GymBranch } from "../models/gym.model";
import { hasManagePermission } from "@modules/auth/utils/role.utils";
import { AuthArgs } from "../types/auth.types";

export function useBranchActions(
  setBranches: React.Dispatch<React.SetStateAction<GymBranch[]>>,
  refresh: () => Promise<void>,
  authArgs: AuthArgs,
) {
  const createBranch = useCallback(
    async (
      data: Omit<GymBranch, "id" | "createdAt" | "updatedAt">,
      openingHoursOverride?: Record<
        string,
        { start_time: string; end_time: string }
      >,
    ) => {
      if (!hasManagePermission(authArgs.auth)) {
        throw new Error("Bạn không có quyền tạo chi nhánh.");
      }
      try {
        await gymPartnerService.createBranch(
          data,
          authArgs,
          openingHoursOverride,
        );
        // Luôn refresh để lấy dữ liệu mới nhất
        await refresh();
        return true;
      } catch (error: any) {
        throw new Error(error.message || "Tạo chi nhánh thất bại");
      }
    },
    [authArgs, refresh],
  );

  const updateBranch = useCallback(
    async (
      id: string,
      data: Partial<GymBranch>,
      openingHoursOverride?: Record<
        string,
        { start_time: string; end_time: string }
      >,
    ) => {
      if (!hasManagePermission(authArgs.auth)) {
        throw new Error("Bạn không có quyền cập nhật chi nhánh.");
      }

      try {
        // Gửi request update
        await gymPartnerService.updateBranch(
          id,
          data,
          authArgs,
          openingHoursOverride,
        );
        // Luôn refresh để lấy dữ liệu mới nhất từ server
        await refresh();
        return true;
      } catch (error: any) {
        // Nếu lỗi, vẫn refresh để đồng bộ (có thể server đã update nhưng trả về lỗi)
        await refresh();
        throw new Error(error.message || "Cập nhật chi nhánh thất bại");
      }
    },
    [authArgs, refresh],
  );

  const deleteBranch = useCallback(
    async (id: string) => {
      if (!hasManagePermission(authArgs.auth)) {
        throw new Error("Bạn không có quyền xóa chi nhánh.");
      }
      try {
        await gymPartnerService.deleteBranch(id, authArgs);
        // Refresh sau khi xóa
        await refresh();
        return true;
      } catch (error: any) {
        await refresh();
        throw new Error(error.message || "Xóa chi nhánh thất bại");
      }
    },
    [authArgs, refresh],
  );

  return { createBranch, updateBranch, deleteBranch };
}
